import { Search } from 'js-search';

import {
  AbstractMentionResource,
  MentionDescription,
  MentionNameDetails,
  MentionNameResolver,
  MentionNameStatus,
  MentionProvider,
  MentionsResult,
  ResolvingMentionProvider,
  SLI_EVENT_TYPE
} from '@atlaskit/mention/resource';
import {
  InviteExperimentCohort,
  InviteFlow,
  UserRole
} from '@atlaskit/mention';
import mentionData from './mention-data';

export interface SandboxMentionConfig {
  minWait?: number;
  maxWait?: number;
  mentionNameResolver?: MentionNameResolver;
  inviteExperimentCohort?: InviteExperimentCohort;
  productName?: string;
  shouldEnableInvite?: boolean;
  onInviteItemClick?: (flow: InviteFlow) => void;
  userRole?: UserRole;
}

export class SandboxMentionResource
  extends AbstractMentionResource
  implements ResolvingMentionProvider, MentionProvider
{
  private config: SandboxMentionConfig;
  private lastReturnedSearch: number;
  private search: Search = new Search('id');

  productName?: string;
  shouldEnableInvite: boolean;
  userRole: UserRole;
  inviteExperimentCohort?: InviteExperimentCohort;
  onInviteItemClick?: (flow: InviteFlow) => void;

  constructor(config: SandboxMentionConfig) {
    super();

    this.search.addIndex('name');
    this.search.addIndex('mentionName');
    this.search.addIndex('nickname');
    this.search.addDocuments(mentionData.mentions);

    this.config = config;
    this.lastReturnedSearch = 0;
    this.productName = config.productName;
    this.shouldEnableInvite = !!config.shouldEnableInvite;
    this.inviteExperimentCohort = config.inviteExperimentCohort;
    this.onInviteItemClick = config.onInviteItemClick;
    this.userRole = config.userRole || 'basic';
  }

  filter(query: string): Promise<void> {
    const searchTime = Date.now();
    const notify = (mentions: MentionsResult) => {
      if (searchTime >= this.lastReturnedSearch) {
        this.lastReturnedSearch = searchTime;
        const stats: { teamMentionDuration?: number; duration?: number } = {};
        if (query === 'team') {
          stats.teamMentionDuration = 200;
        } else {
          stats.duration = 100;
        }
        this._notifyListeners(mentions, stats);
      }
      this._notifyAllResultsListeners(mentions);
    };

    const notifyAnalytics = (
      eventType: string,
      sliName: string,
      action: string
    ) => {
      this._notifyAnalyticsListeners(eventType, sliName, action);
    };

    const minWait = this.config.minWait || 0;
    const randomTime = (this.config.maxWait || 0) - minWait;
    const waitTime = Math.random() * randomTime + minWait;
    setTimeout(() => {
      let mentions: MentionDescription[];
      if (query) {
        mentions = this.search.search(query) as MentionDescription[];
      } else {
        mentions = mentionData.mentions;
      }
      notify({ mentions, query });
      notifyAnalytics(SLI_EVENT_TYPE, 'searchUser', 'succeeded');
    }, waitTime + 1);
    return Promise.resolve();
  }

  recordMentionSelection(): void {
    return;
  }

  resolveMentionName(
    id: string
  ): Promise<MentionNameDetails> | MentionNameDetails {
    if (!this.config.mentionNameResolver) {
      return {
        id,
        name: '',
        status: MentionNameStatus.UNKNOWN
      };
    }
    return this.config.mentionNameResolver.lookupName(id);
  }

  cacheMentionName(id: string, name: string) {
    if (this.config.mentionNameResolver) {
      this.config.mentionNameResolver.cacheName(id, name);
    }
  }

  supportsMentionNameResolving() {
    return !!this.config.mentionNameResolver;
  }

  shouldHighlightMention(mention: MentionDescription): boolean {
    return mention.id === 'sandbox';
  }
}

export const mentionResourceProvider = new SandboxMentionResource({
  minWait: 10,
  maxWait: 25
});
